/**
 * DOS518 - Digital Operations System for Wildfire Management
 * 
 * Copyright (c) 2024-2025 Angelo Irtilaç
 * All rights reserved.
 * 
 * This software is proprietary and confidential.
 * Unauthorized copying, distribution, modification, or use is strictly prohibited.
 * 
 * ---
 * 
 * Author: Angelo Irtilaç
 * Qualifications: DOS (Direttore delle Operazioni di Spegnimento)
 *                39 years - Corpo Nazionale dei Vigili del Fuoco
 *                DOS Trainer & Instructor
 * 
 * Location: Carpi, Italy
 * Contact: [inserire email]
 * 
 * ---
 * 
 * This software implements official Italian Fire Department protocols:
 * - C.O.A.U. (Centro Operativo Aereo Unificato)
 * - SI.TA.C. (Sistema Tattico di Coordinamento)
 * - L.A.C.E.S. (Lookout, Awareness, Communication, Escape routes, Safety zones)
 * 
 * Developed with operational expertise acquired through decades of wildfire
 * emergency management and coordination of suppression operations.
 * 
 * For commercial licensing inquiries, please contact the author.
 */
import React, { useState, useRef, useEffect } from 'react';
import { indexedDBManager } from '../../utils/indexedDBManager';
import { imageCompressor } from '../../utils/imageCompressor';
import { VideoRecorder } from './VideoRecorder';
import './MediaTab.css';

interface MediaItem {
  id: string;
  type: 'photo' | 'video';
  url: string;
  thumbnail?: string;
  category: 'initial' | 'progression' | 'resources' | 'personnel' | 'damage' | 'outcome' | 'other';
  description: string;
  location: string;
  operator: string;
  timestamp: string;
}

interface MediaTabProps {
  mediaItems: MediaItem[];
  onAddMedia: (media: Omit<MediaItem, 'id' | 'timestamp'>) => void;
  onDeleteMedia: (id: string) => void;
  onUpdateMedia?: (id: string, updates: Partial<MediaItem>) => void;
}

const MEDIA_CATEGORIES = [
  { value: 'initial', label: '🔴 Situazione Iniziale', icon: '🔴' },
  { value: 'progression', label: '🟠 Progressione', icon: '🟠' },
  { value: 'resources', label: '🔵 Mezzi/Risorse', icon: '🔵' },
  { value: 'personnel', label: '🟢 Personale', icon: '🟢' },
  { value: 'damage', label: '🟣 Danni', icon: '🟣' },
  { value: 'outcome', label: '🔷 Esito', icon: '🔷' },
  { value: 'other', label: '⚪ Altro', icon: '⚪' }
];

export default function MediaTab({ mediaItems, onAddMedia, onDeleteMedia }: MediaTabProps) {
  const [selectedCategory, setSelectedCategory] = useState<string>('all');
  const [selectedMedia, setSelectedMedia] = useState<MediaItem | null>(null);
  const [showUploadModal, setShowUploadModal] = useState(false);
  const [captureMode, setCaptureMode] = useState<'photo' | 'video' | null>(null);
  const [showVideoRecorder, setShowVideoRecorder] = useState(false);
  const [isVideoReady, setIsVideoReady] = useState(false);
  const [capturedPhotoData, setCapturedPhotoData] = useState<{
    dataURL: string;
    gpsCoords: { lat: number; lng: number } | null;
  } | null>(null);
  
  const [newMediaData, setNewMediaData] = useState({
    type: 'photo' as 'photo' | 'video',
    category: 'initial' as MediaItem['category'],
    description: '',
    location: '',
    operator: '',
    gpsCoords: null as { lat: number; lng: number } | null
  });
  
  const fileInputRef = useRef<HTMLInputElement>(null);
  const videoInputRef = useRef<HTMLInputElement>(null);
  const webcamVideoRef = useRef<HTMLVideoElement>(null);
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const videoReadyCheckInterval = useRef<NodeJS.Timeout | null>(null);
  const [stream, setStream] = useState<MediaStream | null>(null);

  // Cleanup quando il componente smonta
  useEffect(() => {
    return () => {
      if (videoReadyCheckInterval.current) {
        clearInterval(videoReadyCheckInterval.current);
      }
      if (stream) {
        stream.getTracks().forEach(track => track.stop());
      }
    };
  }, [stream]);

  const filteredMedia = selectedCategory === 'all' 
    ? mediaItems 
    : mediaItems.filter(item => item.category === selectedCategory);

  const getCategoryCount = (category: string) => {
    return mediaItems.filter(item => item.category === category).length;
  };

  // Acquisisce coordinate GPS automaticamente
  const acquireGPS = async (): Promise<{ lat: number; lng: number } | null> => {
    return new Promise((resolve) => {
      if (!navigator.geolocation) {
        console.log('⚠️ Geolocalizzazione non supportata');
        resolve(null);
        return;
      }

      navigator.geolocation.getCurrentPosition(
        (position) => {
          const coords = {
            lat: position.coords.latitude,
            lng: position.coords.longitude
          };
          console.log(`✅ GPS acquisito: ${coords.lat}, ${coords.lng}`);
          resolve(coords);
        },
        (error) => {
          console.warn('⚠️ Errore GPS:', error.message);
          resolve(null);
        },
        {
          enableHighAccuracy: true,
          timeout: 10000,
          maximumAge: 0
        }
      );
    });
  };

  // Upload da file CON GPS e WATERMARK
  const handleFileSelect = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const files = event.target.files;
    if (!files || files.length === 0) return;

    // Acquisisce GPS UNA VOLTA per tutti i file caricati insieme
    const gpsCoords = await acquireGPS();
    const gpsLocation = gpsCoords 
      ? `GPS: ${gpsCoords.lat.toFixed(6)}, ${gpsCoords.lng.toFixed(6)}`
      : '';

    for (let i = 0; i < files.length; i++) {
      const file = files[i];
      const isImage = file.type.startsWith('image/');
      const type = isImage ? 'photo' : 'video';
      const id = Date.now().toString() + '-' + i;
      
      try {
        let fileToSave = file;
        let url: string;

        // WATERMARK per le immagini
        if (isImage) {
          // Converti file in data URL per evitare problemi di sicurezza con blob URL
          const reader = new FileReader();
          const fileDataURL = await new Promise<string>((resolve) => {
            reader.onload = (e) => resolve(e.target?.result as string);
            reader.readAsDataURL(file);
          });

          const watermarkText = [
            'DOS518',
            new Date().toLocaleString('it-IT'),
            newMediaData.operator || 'DOS',
            gpsLocation
          ].filter(Boolean).join(' | ');

          const watermarkedURL = await imageCompressor.addWatermark(fileDataURL, watermarkText, {
            position: 'bottom-right',
            fontSize: 14,
            color: '#ff6b35',
            opacity: 0.9,
            logoPath: '/images/LogoIrtilac_TM.png',
            logoHeight: 35
          });

          // Converti watermarked dataURL in blob
          const watermarkedBlob = await fetch(watermarkedURL).then(r => r.blob());
          fileToSave = new File([watermarkedBlob], file.name, { type: 'image/jpeg' });
          url = watermarkedURL; // USA DATA URL, non blob URL

          console.log(`✅ Watermark aggiunto a ${file.name}`);
        } else {
          // Per i video, usa blob URL (va bene per i video)
          url = URL.createObjectURL(file);
        }
        
        // Salva in IndexedDB
        await indexedDBManager.saveMedia(id, fileToSave);
        
        // Combina località manuale + GPS
        const finalLocation = newMediaData.location 
          ? (gpsLocation ? `${newMediaData.location} (${gpsLocation})` : newMediaData.location)
          : gpsLocation;
        
        // Aggiungi ai metadati
        onAddMedia({
          type,
          url,
          category: newMediaData.category,
          description: newMediaData.description || `${type === 'photo' ? 'Foto' : 'Video'} caricato`,
          location: finalLocation,
          operator: newMediaData.operator
        });
        
        console.log(`✅ ${type} salvato: ${file.name} (${fileToSave.size} bytes)`);
      } catch (error) {
        console.error('❌ Errore salvataggio file:', error);
        alert('Errore durante il salvataggio del file');
      }
    }

    setShowUploadModal(false);
    resetForm();
  };

  // Cattura da webcam
  const startWebcam = async (type: 'photo' | 'video') => {
    console.log('🚀 startWebcam chiamata per tipo:', type);
    
    try {
      setIsVideoReady(false);
      console.log('✅ isVideoReady impostato a false');
      
      console.log('📹 Richiedo permessi getUserMedia...');
      const mediaStream = await navigator.mediaDevices.getUserMedia({ 
        video: true, 
        audio: type === 'video' 
      });
      console.log('✅ getUserMedia completato, stream ricevuto');
      
      setStream(mediaStream);
      setCaptureMode(type);
      console.log('✅ Stream e captureMode impostati, useEffect gestirà il video element');
      
    } catch (err) {
      console.error('❌❌❌ Errore in startWebcam:', err);
      alert('Impossibile accedere alla webcam. Verifica i permessi del browser.');
    }
  };

  // useEffect per gestire lo stream quando è disponibile
  useEffect(() => {
    if (stream && captureMode && webcamVideoRef.current) {
      console.log('🎬 useEffect: collego stream al video element');
      
      webcamVideoRef.current.srcObject = stream;
      webcamVideoRef.current.play().then(() => {
        console.log('✅ play() completato');
        
        // POLLING: controlla ogni 200ms se il video è pronto
        let attempts = 0;
        const maxAttempts = 25;
        
        videoReadyCheckInterval.current = setInterval(() => {
          attempts++;
          
          if (webcamVideoRef.current && 
              webcamVideoRef.current.videoWidth > 0 && 
              webcamVideoRef.current.videoHeight > 0) {
            
            console.log('✅ Video pronto:', webcamVideoRef.current.videoWidth, 'x', webcamVideoRef.current.videoHeight);
            setIsVideoReady(true);
            if (videoReadyCheckInterval.current) {
              clearInterval(videoReadyCheckInterval.current);
              videoReadyCheckInterval.current = null;
            }
            
          } else if (attempts >= maxAttempts) {
            console.error('❌ Timeout: video non si è inizializzato');
            alert('Errore: la webcam non si è inizializzata correttamente. Riprova.');
            if (videoReadyCheckInterval.current) {
              clearInterval(videoReadyCheckInterval.current);
              videoReadyCheckInterval.current = null;
            }
            stopWebcam();
          } else {
            console.log(`⏳ Tentativo ${attempts}/${maxAttempts}...`);
          }
        }, 200);
      }).catch(err => {
        console.error('❌ Errore play():', err);
      });
    }
  }, [stream, captureMode]);

  const capturePhoto = async () => {
    console.log('📸 INIZIO CATTURA FOTO - BUTTON CLICCATO!');
    
    // Debug dettagliato
    const hasVideo = !!webcamVideoRef.current;
    const hasCanvas = !!canvasRef.current;
    const hasStream = !!stream;
    
    console.log('🔍 hasVideo:', hasVideo);
    console.log('🔍 hasCanvas:', hasCanvas);
    console.log('🔍 hasStream:', hasStream);
    
    if (!hasVideo) {
      console.error('❌ VIDEO REF MANCANTE!');
      alert('ERRORE: Video ref non trovato. Il video è attivo?');
      return;
    }
    
    if (!hasCanvas) {
      console.error('❌ CANVAS REF MANCANTE!');
      alert('ERRORE: Canvas ref non trovato');
      return;
    }
    
    console.log('✅ Refs OK, procedo con cattura...');
    
    const video = webcamVideoRef.current!;
    const canvas = canvasRef.current!;
    
    console.log('📹 Video dimensioni:', video.videoWidth, 'x', video.videoHeight);
    
    if (video.videoWidth === 0 || video.videoHeight === 0) {
      console.error('❌ Video non pronto');
      alert('Errore: video non ancora pronto. Riprova tra un secondo.');
      return;
    }
    
    canvas.width = video.videoWidth;
    canvas.height = video.videoHeight;
    
    const ctx = canvas.getContext('2d');
    if (!ctx) {
      console.error('❌ Impossibile ottenere context 2D');
      alert('Errore tecnico nel canvas');
      return;
    }
    
    try {
      ctx.drawImage(video, 0, 0);
      console.log('✅ Immagine catturata su canvas');
      
      // Acquisisce GPS
      console.log('🌍 Acquisizione GPS...');
      const gpsCoords = await acquireGPS();
      console.log('✅ GPS:', gpsCoords || 'non disponibile');
      
      // Converti canvas in dataURL (senza watermark per ora)
      const dataURL = canvas.toDataURL('image/jpeg', 0.95);
      console.log('✅ DataURL generato:', dataURL.substring(0, 50) + '...');
      
      // Salva temporaneamente foto e GPS
      setCapturedPhotoData({
        dataURL,
        gpsCoords
      });
      
      // Ferma webcam e apri form per i dati
      stopWebcam();
      setShowUploadModal(true); // Ora mostra il form
      
      console.log('✅ Foto catturata! Ora inserisci i dati...');
      
    } catch (error) {
      console.error('❌❌❌ Errore salvataggio foto webcam:', error);
      alert('Errore durante il salvataggio della foto: ' + (error as Error).message);
    }
  };

  const stopWebcam = () => {
    if (stream) {
      stream.getTracks().forEach(track => track.stop());
      setStream(null);
    }
    if (videoReadyCheckInterval.current) {
      clearInterval(videoReadyCheckInterval.current);
      videoReadyCheckInterval.current = null;
    }
    setCaptureMode(null);
    setIsVideoReady(false);
  };

  const resetForm = () => {
    setNewMediaData({
      type: 'photo',
      category: 'initial',
      description: '',
      location: '',
      operator: '',
      gpsCoords: null
    });
    setCapturedPhotoData(null); // Reset anche foto catturata
  };

  // Salva foto catturata con i dati del form
  const saveCapturedPhoto = async () => {
    if (!capturedPhotoData) {
      alert('Nessuna foto da salvare');
      return;
    }

    try {
      const { dataURL, gpsCoords } = capturedPhotoData;
      
      const gpsLocation = gpsCoords 
        ? `GPS: ${gpsCoords.lat.toFixed(6)}, ${gpsCoords.lng.toFixed(6)}`
        : '';

      // WATERMARK con i dati inseriti
      const watermarkText = [
        'DOS518',
        new Date().toLocaleString('it-IT'),
        newMediaData.operator || 'DOS',
        gpsLocation
      ].filter(Boolean).join(' | ');
      
      console.log('🏷️ Tentativo applicazione watermark...');
      console.log('📝 Testo watermark:', watermarkText);
      console.log('🖼️ DataURL length:', dataURL.length);
      
      const watermarkedURL = await imageCompressor.addWatermark(dataURL, watermarkText, {
        position: 'bottom-right',
        fontSize: 14,
        color: '#ff6b35',
        opacity: 0.9,
        logoPath: '/images/LogoIrtilac_TM.png',
        logoHeight: 35
      });
      
      console.log('✅ Watermark applicato!');
      console.log('🖼️ WatermarkedURL length:', watermarkedURL.length);
      console.log('🔍 Watermark diverso da originale?', watermarkedURL !== dataURL);

      // Converti in file e salva
      const blob = await fetch(watermarkedURL).then(r => r.blob());
      const id = Date.now().toString();
      const file = new File([blob], `webcam-${id}.jpg`, { type: 'image/jpeg' });
      
      await indexedDBManager.saveMedia(id, file);
      
      // Combina località + GPS
      const finalLocation = newMediaData.location 
        ? (gpsLocation ? `${newMediaData.location} (${gpsLocation})` : newMediaData.location)
        : gpsLocation;
      
      onAddMedia({
        type: 'photo',
        url: watermarkedURL,
        category: newMediaData.category,
        description: newMediaData.description || 'Foto da webcam',
        location: finalLocation,
        operator: newMediaData.operator
      });
      
      console.log(`✅✅✅ Foto salvata con successo! (${file.size} bytes)`);
      
      setShowUploadModal(false);
      resetForm();
      
    } catch (error) {
      console.error('❌ Errore salvataggio foto:', error);
      alert('Errore durante il salvataggio: ' + (error as Error).message);
    }
  };

  // Gestisce video registrato
  const handleVideoRecorded = async (blob: Blob) => {
    const id = Date.now().toString();
    const file = new File([blob], `video-${id}.webm`, { type: 'video/webm' });
    
    try {
      // Acquisisce GPS
      const gpsCoords = await acquireGPS();
      const gpsLocation = gpsCoords 
        ? `GPS: ${gpsCoords.lat.toFixed(6)}, ${gpsCoords.lng.toFixed(6)}`
        : '';
      
      // Salva in IndexedDB
      await indexedDBManager.saveMedia(id, file);
      
      // Crea URL temporaneo
      const url = URL.createObjectURL(blob);
      
      // Combina località + GPS
      const finalLocation = newMediaData.location 
        ? (gpsLocation ? `${newMediaData.location} (${gpsLocation})` : newMediaData.location)
        : gpsLocation;
      
      onAddMedia({
        type: 'video',
        url,
        category: newMediaData.category,
        description: newMediaData.description || 'Video registrato',
        location: finalLocation,
        operator: newMediaData.operator
      });
      
      console.log(`✅ Video salvato: ${file.size} bytes`);
      
      setShowVideoRecorder(false);
      setShowUploadModal(false);
      resetForm();
    } catch (error) {
      console.error('❌ Errore salvataggio video:', error);
      alert('Errore durante il salvataggio del video');
    }
  };

  // Export CSV
  const exportMediaReport = () => {
    if (mediaItems.length === 0) {
      alert('Nessun media da esportare');
      return;
    }

    const report = mediaItems.map(item => ({
      Tipo: item.type === 'photo' ? 'Foto' : 'Video',
      Categoria: MEDIA_CATEGORIES.find(c => c.value === item.category)?.label.replace(/[🔴🟠🔵🟢🟣🔷⚪]\s/, ''),
      Descrizione: item.description,
      Località: item.location,
      Operatore: item.operator,
      Data: new Date(item.timestamp).toLocaleString('it-IT')
    }));

    const csv = [
      Object.keys(report[0]).join(','),
      ...report.map(row => Object.values(row).map(val => `"${val}"`).join(','))
    ].join('\n');

    const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `media-report-${new Date().toISOString().split('T')[0]}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  };

  return (
    <div className="media-tab">
      <h2>🖼️ Gestione Foto e Video</h2>

      {/* Barra azioni principali */}
      <div className="media-actions">
        <button className="btn btn-primary" onClick={() => setShowUploadModal(true)}>
          📤 Carica File
        </button>
        <button className="btn btn-success" onClick={() => {
          startWebcam('photo');
        }}>
          📸 Scatta Foto
        </button>
        <button className="btn btn-secondary" onClick={() => {
          setShowUploadModal(true);
          setShowVideoRecorder(true);
        }}>
          🎥 Registra Video
        </button>
        {mediaItems.length > 0 && (
          <button className="btn btn-info" onClick={exportMediaReport}>
            💾 Esporta Report
          </button>
        )}
      </div>

      {/* Filtri categorie */}
      <div className="media-filters">
        <button 
          className={`filter-btn ${selectedCategory === 'all' ? 'active' : ''}`}
          onClick={() => setSelectedCategory('all')}
        >
          📁 Tutti ({mediaItems.length})
        </button>
        {MEDIA_CATEGORIES.map(cat => (
          <button
            key={cat.value}
            className={`filter-btn ${selectedCategory === cat.value ? 'active' : ''}`}
            onClick={() => setSelectedCategory(cat.value)}
          >
            {cat.label} ({getCategoryCount(cat.value)})
          </button>
        ))}
      </div>

      {/* Griglia media */}
      <div className="media-grid">
        {filteredMedia.length === 0 ? (
          <div className="empty-state">
            <div className="empty-icon">📁</div>
            <h3>Nessun media disponibile</h3>
            <p>Carica foto o video per documentare l'operazione</p>
          </div>
        ) : (
          filteredMedia.map(item => (
            <div key={item.id} className="media-card" onClick={() => setSelectedMedia(item)}>
              <div className="media-preview">
                {item.type === 'photo' ? (
                  <img src={item.url} alt={item.description} />
                ) : (
                  <video src={item.url} />
                )}
                <div className="media-type-badge">
                  {item.type === 'photo' ? '📷' : '🎥'}
                </div>
              </div>
              <div className="media-info">
                <div className="media-category">
                  {MEDIA_CATEGORIES.find(c => c.value === item.category)?.label}
                </div>
                <p className="media-description">{item.description}</p>
                {item.location && (
                  <p className="media-location">📍 {item.location}</p>
                )}
                <p className="media-timestamp">
                  🕐 {new Date(item.timestamp).toLocaleString('it-IT')}
                </p>
              </div>
              <button 
                className="btn-remove" 
                onClick={(e) => {
                  e.stopPropagation();
                  if (confirm('Eliminare questo media?')) {
                    onDeleteMedia(item.id);
                  }
                }}
              >
                🗑️
              </button>
            </div>
          ))
        )}
      </div>

      {/* Modal Upload */}
      {showUploadModal && (
        <div className="modal-overlay" onClick={() => {
          stopWebcam();
          setShowUploadModal(false);
        }}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h3>{captureMode ? (captureMode === 'photo' ? '📸 Scatta Foto' : '🎥 Registra Video') : '📤 Carica Media'}</h3>
              <button className="modal-close" onClick={() => {
                stopWebcam();
                setShowUploadModal(false);
              }}>×</button>
            </div>

            <div className="modal-body">
              {capturedPhotoData ? (
                // MOSTRA FORM CON ANTEPRIMA FOTO CATTURATA
                <>
                  <div style={{ marginBottom: '15px', textAlign: 'center' }}>
                    <img 
                      src={capturedPhotoData.dataURL} 
                      alt="Foto catturata" 
                      style={{ 
                        maxWidth: '100%', 
                        maxHeight: '300px', 
                        borderRadius: '8px',
                        boxShadow: '0 2px 8px rgba(0,0,0,0.2)'
                      }} 
                    />
                    {capturedPhotoData.gpsCoords && (
                      <p style={{ fontSize: '12px', color: '#666', marginTop: '5px' }}>
                        📍 GPS: {capturedPhotoData.gpsCoords.lat.toFixed(6)}, {capturedPhotoData.gpsCoords.lng.toFixed(6)}
                      </p>
                    )}
                  </div>

                  <div className="form-group">
                    <label>Categoria</label>
                    <select 
                      value={newMediaData.category}
                      onChange={(e) => setNewMediaData({...newMediaData, category: e.target.value as MediaItem['category']})}
                    >
                      {MEDIA_CATEGORIES.map(cat => (
                        <option key={cat.value} value={cat.value}>{cat.label}</option>
                      ))}
                    </select>
                  </div>

                  <div className="form-group">
                    <label>Descrizione</label>
                    <textarea
                      value={newMediaData.description}
                      onChange={(e) => setNewMediaData({...newMediaData, description: e.target.value})}
                      placeholder="Descrivi cosa mostra..."
                      rows={3}
                    />
                  </div>

                  <div className="form-group">
                    <label>Località</label>
                    <input
                      type="text"
                      value={newMediaData.location}
                      onChange={(e) => setNewMediaData({...newMediaData, location: e.target.value})}
                      placeholder="Es: Settore A, lato est"
                    />
                  </div>

                  <div className="form-group">
                    <label>Operatore</label>
                    <input
                      type="text"
                      value={newMediaData.operator}
                      onChange={(e) => setNewMediaData({...newMediaData, operator: e.target.value})}
                      placeholder="Nome e cognome"
                    />
                  </div>

                  <div style={{ display: 'flex', gap: '10px' }}>
                    <button 
                      className="btn btn-success" 
                      style={{ flex: 1 }}
                      onClick={saveCapturedPhoto}
                    >
                      💾 Salva Foto
                    </button>
                    <button 
                      className="btn btn-danger"
                      onClick={() => {
                        setCapturedPhotoData(null);
                        setShowUploadModal(false);
                        resetForm();
                      }}
                    >
                      ❌ Annulla
                    </button>
                  </div>
                </>
              ) : captureMode ? (
                // MOSTRA WEBCAM PER CATTURA
                <div className="webcam-container">
                  <video ref={webcamVideoRef} autoPlay playsInline />
                  <canvas ref={canvasRef} style={{ display: 'none' }} />
                  
                  {/* Indicatore stato video */}
                  {!isVideoReady && (
                    <div style={{
                      position: 'absolute',
                      top: '50%',
                      left: '50%',
                      transform: 'translate(-50%, -50%)',
                      background: 'rgba(0,0,0,0.7)',
                      color: 'white',
                      padding: '10px 20px',
                      borderRadius: '5px',
                      fontSize: '14px'
                    }}>
                      ⏳ Inizializzazione camera...
                    </div>
                  )}
                  
                  <div className="webcam-controls">
                    {captureMode === 'photo' && (
                      <button 
                        className="btn btn-success" 
                        onClick={capturePhoto}
                        disabled={!isVideoReady}
                        style={{ opacity: isVideoReady ? 1 : 0.5 }}
                      >
                        📸 {isVideoReady ? 'Cattura' : 'Attendere...'}
                      </button>
                    )}
                    <button className="btn btn-danger" onClick={stopWebcam}>
                      ❌ Annulla
                    </button>
                  </div>
                </div>
              ) : (
                // MOSTRA FORM PER UPLOAD FILE
                <>
                  <div className="form-group">
                    <label>Categoria</label>
                    <select 
                      value={newMediaData.category}
                      onChange={(e) => setNewMediaData({...newMediaData, category: e.target.value as MediaItem['category']})}
                    >
                      {MEDIA_CATEGORIES.map(cat => (
                        <option key={cat.value} value={cat.value}>{cat.label}</option>
                      ))}
                    </select>
                  </div>

                  <div className="form-group">
                    <label>Descrizione</label>
                    <textarea
                      value={newMediaData.description}
                      onChange={(e) => setNewMediaData({...newMediaData, description: e.target.value})}
                      placeholder="Descrivi cosa mostra..."
                      rows={3}
                    />
                  </div>

                  <div className="form-group">
                    <label>Località</label>
                    <input
                      type="text"
                      value={newMediaData.location}
                      onChange={(e) => setNewMediaData({...newMediaData, location: e.target.value})}
                      placeholder="Es: Settore A, lato est"
                    />
                  </div>

                  <div className="form-group">
                    <label>Operatore</label>
                    <input
                      type="text"
                      value={newMediaData.operator}
                      onChange={(e) => setNewMediaData({...newMediaData, operator: e.target.value})}
                      placeholder="Nome e cognome"
                    />
                  </div>

                  <input
                    ref={fileInputRef}
                    type="file"
                    accept="image/*,video/*"
                    multiple
                    style={{ display: 'none' }}
                    onChange={handleFileSelect}
                  />

                  <button 
                    className="btn btn-primary btn-block"
                    onClick={() => fileInputRef.current?.click()}
                  >
                    📁 Seleziona File
                  </button>
                </>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Modal Dettaglio */}
      {selectedMedia && (
        <div className="modal-overlay" onClick={() => setSelectedMedia(null)}>
          <div className="modal-content modal-detail" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h3>Dettagli Media</h3>
              <button className="modal-close" onClick={() => setSelectedMedia(null)}>×</button>
            </div>

            <div className="modal-body">
              <div className="media-preview-large">
                {selectedMedia.type === 'photo' ? (
                  <img src={selectedMedia.url} alt={selectedMedia.description} />
                ) : (
                  <video src={selectedMedia.url} controls />
                )}
              </div>

              <div className="media-details">
                <div className="detail-row">
                  <strong>Categoria:</strong>
                  <span>{MEDIA_CATEGORIES.find(c => c.value === selectedMedia.category)?.label}</span>
                </div>
                <div className="detail-row">
                  <strong>Descrizione:</strong>
                  <span>{selectedMedia.description}</span>
                </div>
                {selectedMedia.location && (
                  <div className="detail-row">
                    <strong>Località:</strong>
                    <span>{selectedMedia.location}</span>
                  </div>
                )}
                {selectedMedia.operator && (
                  <div className="detail-row">
                    <strong>Operatore:</strong>
                    <span>{selectedMedia.operator}</span>
                  </div>
                )}
                <div className="detail-row">
                  <strong>Data/Ora:</strong>
                  <span>{new Date(selectedMedia.timestamp).toLocaleString('it-IT')}</span>
                </div>
              </div>

              <button 
                className="btn btn-danger btn-block"
                onClick={() => {
                  if (confirm('Eliminare questo media?')) {
                    onDeleteMedia(selectedMedia.id);
                    setSelectedMedia(null);
                  }
                }}
              >
                🗑️ Elimina
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Video Recorder */}
      {showVideoRecorder && (
        <VideoRecorder
          onVideoRecorded={handleVideoRecorded}
          onClose={() => {
            setShowVideoRecorder(false);
            setShowUploadModal(false);
          }}
          maxDuration={300}
        />
      )}

      {/* Webcam Overlay Full-Screen per cattura foto */}
      {captureMode === 'photo' && !showUploadModal && (
        <div className="modal-overlay">
          <div className="webcam-fullscreen">
            <video ref={webcamVideoRef} autoPlay playsInline style={{ 
              width: '100%', 
              height: '100%', 
              objectFit: 'contain' 
            }} />
            <canvas ref={canvasRef} style={{ display: 'none' }} />
            
            {!isVideoReady && (
              <div style={{
                position: 'absolute',
                top: '50%',
                left: '50%',
                transform: 'translate(-50%, -50%)',
                background: 'rgba(0,0,0,0.7)',
                color: 'white',
                padding: '15px 30px',
                borderRadius: '8px',
                fontSize: '18px'
              }}>
                ⏳ Inizializzazione camera...
              </div>
            )}
            
            <div style={{
              position: 'absolute',
              bottom: '30px',
              left: '50%',
              transform: 'translateX(-50%)',
              display: 'flex',
              gap: '15px'
            }}>
              <button 
                className="btn btn-success" 
                onClick={capturePhoto}
                disabled={!isVideoReady}
                style={{ 
                  opacity: isVideoReady ? 1 : 0.5,
                  padding: '15px 30px',
                  fontSize: '18px'
                }}
              >
                📸 {isVideoReady ? 'Cattura' : 'Attendere...'}
              </button>
              <button 
                className="btn btn-danger" 
                onClick={stopWebcam}
                style={{
                  padding: '15px 30px',
                  fontSize: '18px'
                }}
              >
                ❌ Annulla
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
